import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../config/api';
import OrderMessages from './OrderMessages';
import './FloatingMessageButton.css';

const FloatingMessageButton = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const shouldShow = user &&
                     !location.pathname.startsWith('/admin') &&
                     orders.length > 0;

  useEffect(() => {
    if (user && !showPicker && !selectedOrder) {
      fetchOrders();
      const interval = setInterval(() => {
        if (!showPicker && !selectedOrder) fetchOrders();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user, showPicker, selectedOrder]);

  const fetchOrders = async () => {
    if (!user || showPicker || selectedOrder) return;
    try {
      const response = await axios.get(`${API_URL}/messages/orders`);
      const list = response.data || [];
      setOrders(list);
      const total = list.reduce((sum, o) => sum + (o.unreadCount || 0), 0);
      setUnreadCount(total);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error('Error fetching orders for chat:', error);
      }
      setOrders([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    if (orders.length > 0 && !showPicker && !selectedOrder) {
      setShowPicker(true);
    }
  };

  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
    setShowPicker(false);
  };

  const handleCloseMessages = () => {
    setSelectedOrder(null);
    setTimeout(() => fetchOrders(), 500);
  };

  const handleClosePicker = () => {
    setShowPicker(false);
  };

  const getStatusLabel = (status) => {
    const key = `activeOrder.status.${status}`;
    const label = t(key);
    return label !== key ? label : status;
  };

  const formatLastActivity = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return t('chatPicker.justNow') || 'Just now';
    if (diffMins < 60) return t('chatPicker.minutesAgo', { count: diffMins }) || `${diffMins} min ago`;
    if (diffHours < 24) return t('chatPicker.hoursAgo', { count: diffHours }) || `${diffHours}h ago`;
    if (diffDays < 7) return t('chatPicker.daysAgo', { count: diffDays }) || `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  if (!shouldShow || loading) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="floating-message-button"
        onClick={handleClick}
        aria-label="Messages"
      >
        <i className="fas fa-comments"></i>
        {unreadCount > 0 && (
          <span className="message-badge">{unreadCount}</span>
        )}
      </button>

      {showPicker && orders.length > 0 && (
        <div className="chat-picker-overlay" onClick={handleClosePicker} role="dialog" aria-label={t('chatPicker.title')}>
          <div className="chat-picker-modal" onClick={e => e.stopPropagation()}>
            <div className="chat-picker-header">
              <h3>{t('chatPicker.title')}</h3>
              <button type="button" className="chat-picker-close" onClick={handleClosePicker} aria-label={t('chatPicker.close') || 'Close'}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <p className="chat-picker-subtitle">{t('chatPicker.selectOrder')}</p>
            <ul className="chat-picker-list">
              {orders.map((order) => (
                <li
                  key={order._id}
                  className="chat-picker-item"
                  onClick={() => handleSelectOrder(order)}
                >
                  <div className="chat-picker-item-main">
                    <span className="chat-picker-order-number">#{order.orderNumber || order._id?.slice(-6)}</span>
                    <span className="chat-picker-restaurant">{order.restaurantName || (order.restaurant?.name) || '—'}</span>
                  </div>
                  <div className="chat-picker-item-meta">
                    <span className={`chat-picker-status status-${order.status}`}>{getStatusLabel(order.status)}</span>
                    <span className="chat-picker-activity">{formatLastActivity(order.lastActivityAt || order.updatedAt)}</span>
                  </div>
                  {(order.unreadCount || 0) > 0 && (
                    <span className="chat-picker-unread">{order.unreadCount}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {selectedOrder && (
        <OrderMessages order={selectedOrder} onClose={handleCloseMessages} />
      )}
    </>
  );
};

export default FloatingMessageButton;
